import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { FastlyImage } from '../common/image'

const TOOLS = [
  {
    name: 'GeoGebra',
    image: '/images/courses/math/platform/geogebra.png',
    height: 41,
    width: 175,
  },
  {
    name: 'Polypad',
    image: '/images/courses/math/platform/polypad.png',
    height: 51,
    width: 171,
  },
  {
    name: 'Pencil Spaces',
    image: '/images/courses/math/platform/pencil-spaces.png',
    height: 43,
    width: 228,
  },
  {
    name: 'Topworksheets',
    image: '/images/courses/math/platform/top-worksheets.png',
    height: 24,
    width: 224,
  },
  {
    name: 'Wordwall',
    image: '/images/courses/math/platform/wordwall.png',
    height: 23,
    width: 137,
  },
]

type Feature = {
  pill: string
  title: string
  color: string
  copy: string
  image: string
}

const FEATURES: Feature[] = [
  {
    pill: 'Live 1:1 shared whiteboard',
    title: 'One shared board. Two people. Real time.',
    color: 'text-red-200',
    image: '/images/courses/math/gifs/class-1.gif',
    copy:
      'Your child and their tutor draw, write, and solve on the same canvas at the same time. If your child has a doubt mid-class, they can circle it directly on the board.',
  },
  {
    pill: 'Visual learning tools',
    title: 'See it before the formula.',
    color: 'text-orange',
    image: '/images/courses/math/gifs/class-2.gif',
    copy:
      'Before any formula is introduced, your child works with visual tools like fraction bars, algebra tiles, and weighing scales that make abstract concepts feel concrete.',
  },
  {
    pill: 'Interactive geometry',
    title: 'Drag it. Watch everything change.',
    color: 'text-blue-300',
    image: '/images/courses/math/gifs/class-3.gif',
    copy:
      'Your child can drag any shape on screen and watch every angle, length, and area update in real time. They discover how geometry works by experimenting with it.',
  },
  {
    pill: 'Play-based practice',
    title: 'Practice as play.',
    color: 'text-green-100',
    image: '/images/courses/math/gifs/class-4.gif',
    copy:
      "Practice in every class takes the form of matching exercises, sorting activities, number puzzles, and vocabulary games that hold your child's attention.",
  },
]

type Props = {
  urlQuery?: string
}

const MorePractice = ({ urlQuery }: Props) => {
  return (
    <section className="bg-orange-100 py-12 lg:py-16 px-5">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-4 max-w-4xl text-grey mx-auto">
            More practice in one class than most students get in a week
          </h2>
          <p className="text-base lg:text-lg text-grey max-w-4xl mx-auto">
            In every 45-minute class, your child is the one drawing and solving
            on a shared board with their tutor, exploring abstract concepts
            visually before any formula appears, and practising through fast
            interactive activities. The result: more reps, more thinking, more
            hold-it-in-your-head learning.
          </p>
        </div>

        <div className="text-center mb-10 rounded-2xl md:rounded-full bg-white p-2">
          <p className=" lg:text-xl font-600 text-orange mb-4">
            Best-in-class Math tools, built into your child&apos;s Codingal
            dashboard
          </p>
          <div className="flex flex-wrap justify-center items-center gap-3 lg:gap-5">
            {TOOLS.map((tool) => (
              <FastlyImage
                key={tool.name}
                src={tool.image}
                alt={tool.name}
                width={tool.width}
                height={tool.height}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="bg-white rounded-xl border border-grey-300 shadow-sm p-2 lg:p-4 flex flex-col gap-3 max-w-md"
            >
              <span
                className={`inline-block self-start py-1 rounded-full ${feature.color} text-xs lg:text-sm font-700 uppercase tracking-wide`}
              >
                {feature.pill}
              </span>
              <FastlyImage
                src={feature.image}
                alt={feature.title}
                width={432}
                height={243}
              />
              <h3
                className={`text-xl lg:text-2xl font-600 leading-tight ${feature.color}`}
              >
                {feature.title}
              </h3>
              <p className="text-grey-800 leading-snug">{feature.copy}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10">
          <BookFreeClassBtn query={urlQuery} />
        </div>
      </div>
    </section>
  )
}

export default MorePractice
